import { useState } from 'react';

const TIERS = [
  { key: 'fatalities', label: 'Fatalities', color: '#991B1B', hoverColor: '#7F1D1D' },
  { key: 'lti', label: 'LTI', color: '#DC2626', hoverColor: '#B91C1C' },
  { key: 'rwc', label: 'RWC', color: '#EA580C', hoverColor: '#C2410C' },
  { key: 'mtc', label: 'MTC', color: '#D97706', hoverColor: '#B45309' },
  { key: 'fac', label: 'FAC', color: '#CA8A04', hoverColor: '#A16207' },
  { key: 'nearMiss', label: 'Near Miss', color: '#65A30D', hoverColor: '#4D7C0F' },
];

export function SafetyTriangle({ tiers = [], healthScore = 0, manhours = {} }) {
  const [hoveredTier, setHoveredTier] = useState(null);

  const tierData = TIERS.map((t, i) => {
    const match = tiers.find(d => d.tier === t.label) || {};
    return { ...t, count: match.count || 0, benchmark: match.benchmark || 0, index: i };
  });

  const svgW = 400, svgH = 340;
  const topX = svgW / 2, topY = 20;
  const botL = 30, botR = svgW - 30, botY = svgH - 20;
  const layerCount = tierData.length;

  const getLayerPath = (i) => {
    const t0 = i / layerCount;
    const t1 = (i + 1) / layerCount;
    const x0L = topX + (botL - topX) * t0;
    const x0R = topX + (botR - topX) * t0;
    const y0 = topY + (botY - topY) * t0;
    const x1L = topX + (botL - topX) * t1;
    const x1R = topX + (botR - topX) * t1;
    const y1 = topY + (botY - topY) * t1;
    return { path: `M${x0L},${y0} L${x0R},${y0} L${x1R},${y1} L${x1L},${y1} Z`, cx: svgW / 2, cy: (y0 + y1) / 2, w: x1R - x1L };
  };

  const scoreColor = healthScore >= 80 ? '#22C55E' : healthScore >= 50 ? '#F59E0B' : '#EF4444';

  return (
    <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }} data-testid="safety-triangle">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Heinrich Safety Triangle</h3>
          <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>Cumulative incident pyramid</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-right">
            <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Health Score</div>
            <div className="text-lg font-bold" style={{ color: scoreColor }}>{healthScore}%</div>
          </div>
        </div>
      </div>
      <div className="flex justify-center">
        <svg viewBox={`0 0 ${svgW} ${svgH}`} width="100%" style={{ maxWidth: 380 }}>
          <defs>
            {tierData.map((t) => (
              <linearGradient key={t.key} id={`grad-${t.key}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={t.color} stopOpacity="0.9" />
                <stop offset="100%" stopColor={t.color} stopOpacity="0.7" />
              </linearGradient>
            ))}
          </defs>
          {tierData.map((t) => {
            const { path, cx, cy } = getLayerPath(t.index);
            const isHovered = hoveredTier === t.key;
            return (
              <g key={t.key}
                onMouseEnter={() => setHoveredTier(t.key)}
                onMouseLeave={() => setHoveredTier(null)}
                style={{ cursor: 'pointer' }}>
                <path d={path}
                  fill={isHovered ? t.hoverColor : `url(#grad-${t.key})`}
                  stroke="rgba(255,255,255,0.2)" strokeWidth="1"
                  style={{ transition: 'fill 0.2s' }} />
                <text x={cx} y={cy - 5} textAnchor="middle" fill="white" fontSize="11" fontWeight="600">{t.count}</text>
                <text x={cx} y={cy + 10} textAnchor="middle" fill="rgba(255,255,255,0.8)" fontSize="9">{t.label}</text>
              </g>
            );
          })}
        </svg>
      </div>
      {hoveredTier && (() => {
        const t = tierData.find(d => d.key === hoveredTier);
        return t ? (
          <div className="mt-3 rounded-lg p-3 text-center" style={{ backgroundColor: `${t.color}12`, border: `1px solid ${t.color}30` }}>
            <span className="text-sm font-semibold" style={{ color: t.color }}>{t.label}: {t.count}</span>
            {t.benchmark > 0 && <span className="text-xs ml-2" style={{ color: 'var(--text-muted)' }}>(Benchmark: {t.benchmark})</span>}
          </div>
        ) : null;
      })()}
      {manhours?.cumulative && (
        <div className="mt-3 text-center">
          <span className="text-[10px]" style={{ color: 'var(--text-disabled)' }}>
            Based on {(manhours.cumulative / 1000000).toFixed(2)}M cumulative manhours
          </span>
        </div>
      )}
    </div>
  );
}
